<template>
  <q-responsive
    :ratio="hideImage && button ? 1.6 : 1.05"
    :draggable="button"
    dropzone
    @dragstart="dragStart($event)"
    @dragend="dragEnd"
    @drop="drop"
    @dragover.prevent
    @dragenter="dragEnter"
    @dragleave="dragLeave"
  >
    <q-menu
      v-if="editMode"
      touch-position
      context-menu
    >
      <q-list style="width: 200px;">
        <q-item
          v-close-popup
          :disabled="button"
          clickable
          @click="$emit('add-button', index)"
        >
          <q-item-section>
            Add Button
          </q-item-section>
        </q-item>
        <q-item
          v-close-popup
          :disabled="!button"
          clickable
          @click="$emit('edit-button', button)"
        >
          <q-item-section>
            Edit Button
          </q-item-section>
        </q-item>
        <q-separator />
        <q-item
          v-close-popup
          clickable
          :disabled="!button"
          @click="dragStart($event)"
        >
          <q-item-section>
            Cut Button
          </q-item-section>
        </q-item>
        <q-item
          v-close-popup
          clickable
          :disabled="$parent.dragStartIndex === null"
          @click="drop"
        >
          <q-item-section>
            Paste Button
          </q-item-section>
        </q-item>
        <q-separator />
        <q-item
          v-close-popup
          :disabled="!button"
          clickable
          @click="$emit('delete-button', button)"
        >
          <q-item-section>
            Delete Button
          </q-item-section>
        </q-item>
      </q-list>
    </q-menu>
    <q-menu
      v-if="!editMode && currentUser && currentUser.admin && button"
      touch-position
      context-menu
    >
      <q-list style="width: 200px;">
        <q-item
          v-close-popup
          clickable
          @click="$emit('edit-button', button)"
        >
          <q-item-section avatar>
            <q-icon name="edit" />
          </q-item-section>
          <q-item-section>
            Edit Button
          </q-item-section>
        </q-item>
        <q-separator />
        <q-item
          v-close-popup
          clickable
          @click="$emit('edit-item', button.resolveItem)"
        >
          <q-item-section avatar>
            <q-icon name="fastfood" />
          </q-item-section>
          <q-item-section>
            Edit Item
          </q-item-section>
        </q-item>
        <q-separator />
        <q-item
          v-close-popup
          clickable
          @click="$emit('edit-grid')"
        >
          <q-item-section avatar>
            <q-icon name="grid_on" />
          </q-item-section>
          <q-item-section>
            Edit Grid
          </q-item-section>
        </q-item>
      </q-list>
    </q-menu>
    <q-menu
      v-if="!editMode && currentUser && currentUser.admin && !button"
      touch-position
      context-menu
    >
      <q-list style="width: 200px;">
        <q-item
          v-close-popup
          clickable
          @click="$emit('edit-item')"
        >
          <q-item-section avatar>
            <q-icon name="fastfood" />
          </q-item-section>
          <q-item-section>
            Add Item
          </q-item-section>
        </q-item>
        <q-item
          v-close-popup
          clickable
          @click="$emit('edit-grid')"
        >
          <q-item-section avatar>
            <q-icon name="grid_on" />
          </q-item-section>
          <q-item-section>
            Edit Grid
          </q-item-section>
        </q-item>
      </q-list>
    </q-menu>
    <div
      v-if="!button"
      :class="{ 'drag-over': dragOver, 'cursor-pointer': editMode }"
      class="row justify-center items-center empty text-grey-7 text-h5"
      @click="editMode && $emit('add-button', index)"
    >
      {{ editMode ? '+' : '' }}
    </div>
    <q-btn
      v-else
      v-touch-hold.mouse="handleTouchHold"
      class="button-card rounded-borders overflow-hidden position-relative"
      :class="{
        'drag-over': dragOver,
        'dragging': dragging,
        'q-pa-none': !hideImage,
        [colorClass]: true
      }"
      :style="{ 'max-height': !hideImage ? undefined : '140px' }"
      flat
      :outline="!hideImage"
      dense
      ripple
      no-caps
      @click="buttonClicked"
    >
      <div
        class="column fit"
        :style="{ opacity: hasReducedOpacity(button, { order }) ? 0.3 : 1 }"
      >
        <age-restricted-banner
          v-if="button.showAlcoholAcknowledgement"
        />
        <q-img
          v-if="!hideImage"
          class="col"
          :style="{ 'margin-bottom': isMobile ? '45px' : '30px' }"
          contain
          :ratio="1.6"
          :src="button.imageUrl"
          :error="true"
          :no-spinner="true"
          :no-transition="true"
        >
          <template #default>
            <div
              v-if="!button.imageUrl"
              class="absolute-full text-white text-center text-h3"
              :style="`background-color: ${button.backgroundColor || '#444'}`"
            >
              {{ button.abbreviation }}
            </div>
          </template>
          <template #error>
            <div
              v-if="!button.imageUrl"
              class="absolute-full text-white text-center text-h3"
              :style="`background-color: ${button.backgroundColor || '#444'}`"
            >
              {{ button.abbreviation }}
            </div>
          </template>
        </q-img>

        <div
          class="col row justify-center items-center text-subtitle1 text-bold q-pa-xs full-width"
          :class="{
            [colorClass]: true,
            'ellipsis': !hideImage,
            'absolute-bottom': !hideImage
          }"
          :style="{ height: hideImage ? '100%' : '35px', }"
        >
          <div
            class="full-width text-center"
            :class="{ 'ellipsis': !hideImage }"
            :style="{ 'line-height': hideImage ? '1.1' : undefined }"
          >
            {{ button.name || '+' }}
          </div>
        </div>
      </div>
      <q-badge
        v-if="isAvailabilityBadgeVisible(button, { order })"
        class="absolute-top-right"
        :color="availabilityBadgeColor(button, { order })"
        :label="availableStock(button, { order })"
      />
    </q-btn>
    <toggle-sales-disabled-dialog
      v-if="button && (button.itemVariation || button.item)"
      ref="toggleSalesDisabledDialog"
      :item="button.item"
      :item-variation="button.itemVariation"
      @request-authorization="callback => $emit('request-authorization', callback)"
    />
  </q-responsive>
</template>

<script>
import AgeRestrictedBanner from 'src/modules/checkout/components/AgeRestrictedBanner'
import ToggleSalesDisabledDialog from 'src/modules/checkout/components/ToggleSalesDisabledDialog'
import { useSalesRestrictions } from 'src/modules/register/composables/sales-restrictions/sales-restrictions'
import { availabilityBadgeColor, availableStock, hasReducedOpacity, isAvailabilityBadgeVisible, isOutOfStock } from 'src/services/inventory/sales-restrictions'

export default {
  name: 'RegisterButton',
  components: { AgeRestrictedBanner, ToggleSalesDisabledDialog },
  context: ['currentUser'],
  configuration: ['currentSite'],
  inject: ['order'],
  props: {
    index: {
      type: Number,
      required: true
    },
    button: {
      type: Object,
      required: false,
      default () { return null }
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false
    },
    hideImage: {
      type: Boolean,
      required: false,
      default: false
    },
    bgColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    textColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['add-button', 'edit-button', 'delete-button', 'edit-item', 'edit-grid', 'click', 'drag-end', 'drag-start', 'request-authorization'],
  setup () {
    const { checkForSalesRestrictions } = useSalesRestrictions()

    return {
      checkForSalesRestrictions
    }
  },
  data () {
    return {
      dragOver: 0,
      dragging: false
    }
  },
  computed: {
    colorClass () {
      return `bg-${this.bgColor} text-${this.textColor}`
    }
  },
  created () {
    _.extend(this, {
      availabilityBadgeColor,
      availableStock,
      hasReducedOpacity,
      isAvailabilityBadgeVisible,
      isOutOfStock
    })
  },
  methods: {
    handleTouchHold () {
      if (!this.$register) return

      this.$refs.toggleSalesDisabledDialog?.show()
    },
    dragStart (event) {
      this.$emit('drag-start', this.index)

      // Use set timeout because we want the drag bitmap to be the version prior to adding the dragging class
      setTimeout(() => { this.dragging = true }, 0)
    },
    dragEnd () {
      this.dragging = false
    },
    buttonClicked () {
      this.checkForSalesRestrictions(
        this.button,
        { order: this.order },
        (adminApproved = false) => {
          this.$emit('click', adminApproved)
          // We need to remove focus from the button to prevent scanning with carage return reselecting the button.
          document.activeElement.blur()
        }
      )
    },
    drop () {
      this.$emit('drag-end', this.index)
      this.dragOver = 0
    },
    dragEnter () {
      this.dragOver += 1
    },
    dragLeave () {
      this.dragOver -= 1
    }
  }
}
</script>

<style lang="scss">
.button-card.q-pa-none .q-btn__wrapper {
  padding: 0;
}
</style>

<style lang="scss" scoped>
.empty {
  border: solid 1px $grey-5;
}
.drag-over {
  border: solid 2px $grey-8;
}

.dragging {
  border: solid 1px $grey-5;
}

.button-card.drag-over > div, .button-card.dragging > div {
  opacity: 0.1;
}
</style>
